import { createContext } from 'react';
import type { tLeadCloseAbandonVia } from 'src/hooks/analytics/leadClose/leadCloseAnalytics.constants';
import type { tCloseForm } from './LeadClose.functions';
import type {
  tCloseResult,
  tCloseSettings,
  tCloseSource,
  tCloseSummary,
  tCloseType,
  tRegisterAnswer,
  tSuggestedStage,
} from './LeadClose.types';

export type tLoad<T> = {
  status: 'idle' | 'loading' | 'ready' | 'failed';
  data: T | null;
};

/**
 * What the form needs of a lead, whichever surface opened it: the chat's lead
 * and the board's card are adapted to this (`.claude/rules/modals.md`).
 */
export type tCloseLeadRef = {
  id: string;
  name: string;
  stageName: string | null;
  value: number | null;
};

export type tCloseFormState = {
  /** A new id per opening: an answer for an older opening changes nothing. */
  id: string;
  lead: tCloseLeadRef;
  type: tCloseType;
  source: tCloseSource;
  saving: boolean;
  /** The refusal's reason, or `unavailable` when there was no answer. */
  error: string | null;
  /** The result being corrected (edit mode, from Historial), with the revision it was read at. */
  edit?: tCloseResult;
  /** Where the person is: the chat announces a save with a toast, the board on the board (KTD28). */
  surface: tCloseSurface;
  /** A move into a mapped stage opened this form: the availability prompt waits until it closes. */
  deferAvailability?: boolean;
};

export type tCloseSurface = 'chat' | 'board';

/** A confirmed save in this tab; its id makes each one announce itself once. */
export type tLastSave =
  | {
      id: string;
      kind: 'create';
      leadId: string;
      leadName: string;
      answer: tRegisterAnswer;
      source: tCloseSource;
      surface: tCloseSurface;
    }
  | {
      id: string;
      kind: 'edit';
      leadId: string;
      leadName: string;
      source: tCloseSource;
      surface: tCloseSurface;
    };

/** "Laura sigue en X. ¿La mueves a Y?" — held in memory for the chat (U14). */
export type tCloseSuggestion = {
  leadId: string;
  leadName: string;
  stage: tSuggestedStage;
};

export type tLeadCloseContext = {
  /** The flag has loaded and is on for this workspace. */
  enabled: boolean;
  settings: tLoad<tCloseSettings>;
  summaryOf: (leadId: string) => tLoad<tCloseSummary>;
  loadSummary: (leadId: string) => void;
  /** Reads the summary again even when it is held (another tab changed a result). */
  refreshSummary: (leadId: string) => void;
  reloadSettings: () => void;
  form: tCloseFormState | null;
  openForm: (args: {
    lead: tCloseLeadRef;
    type: tCloseType;
    source: tCloseSource;
    edit?: tCloseResult;
    surface?: tCloseSurface;
    deferAvailability?: boolean;
  }) => void;
  /** Closes the form unsaved; `via` is what closed it, for analytics (`other` when omitted). */
  closeForm: (via?: tLeadCloseAbandonVia) => void;
  submitForm: (form: tCloseForm) => Promise<tRegisterAnswer | null>;
  /** Deletes a result from Historial; the reason of a refusal, or null when it went through. */
  removeResult: (
    leadId: string,
    result: tCloseResult,
  ) => Promise<string | null>;
  lastSave: tLastSave | null;
  suggestion: tCloseSuggestion | null;
  dismissSuggestion: () => void;
  /** Goes up after every edit or deletion, so an open Historial reads again. */
  historyVersion: number;
  /** The stage's mapping as this tab last set it, over what the board holds (U16). */
  stageOutcomeOf: (
    stageId: string,
    held: tCloseType | null | undefined,
  ) => tCloseType | null;
  /** Saves settings (U17) at the held version; the refusal's reason, or null. */
  saveSettings: (body: Record<string, unknown>) => Promise<string | null>;
  /** Maps a stage to a result (null clears); the refusal's reason, or null. */
  mapStage: (
    funnelId: string,
    stageId: string,
    value: tCloseType | null,
    /** The board's copy when the choice was made. */
    held?: tCloseType | null,
  ) => Promise<string | null>;
};

const IDLE = { status: 'idle', data: null } as const;

export const LeadCloseContext = createContext<tLeadCloseContext>({
  enabled: false,
  settings: IDLE,
  summaryOf: () => IDLE,
  loadSummary: () => undefined,
  refreshSummary: () => undefined,
  reloadSettings: () => undefined,
  form: null,
  openForm: () => undefined,
  closeForm: () => undefined,
  submitForm: async () => null,
  removeResult: async () => null,
  lastSave: null,
  suggestion: null,
  dismissSuggestion: () => undefined,
  historyVersion: 0,
  stageOutcomeOf: (_stageId, held) => held ?? null,
  mapStage: async () => null,
  saveSettings: async () => null,
});
